import {makeShopifyRequest} from "../request";
import {log} from "../log";
import {
	addCartLines,
	CartAttribute,
	CLASS_SCHEDULE_AND_BUILDING_FRAGMENT,
	createCart,
	getCart,
	type CartCreationInput,
	type VariantClassSchedule,
} from "./storefront";
import {getCustomerWithCartMetafield, setCustomerCartMetafield} from "./admin";
import type {
	CartAttributesUpdateMutation,
	GetVariantForAddToCartQuery,
} from "../codegen/storefront.generated";
import {
	castToMetaobject,
	castToMetaobjectArray,
	getValue,
} from "../operation-utils";

export type DetailedProductVariant = Awaited<
	ReturnType<typeof getDetailedProductVariant>
>;

export class NoShopifyCustomerError extends Error {
	constructor() {
		super("No Shopify Customer found for shopper");
		this.name = "NoShopifyCustomerError";
	}
}

export function isNoShopifyCustomerError(error: unknown): boolean {
	return error instanceof Error && error.name === "NoShopifyCustomerError";
}

export async function getDetailedProductVariant(variantId: string) {
	const {node} = await makeShopifyRequest<GetVariantForAddToCartQuery>(
		"storefront",
		`#graphql
			query GetVariantForAddToCart($id: ID!) {
				node(id: $id) {
					__typename
					... on ProductVariant {
						id
						availableForSale
						classMetafield: metafield(namespace: "custom", key: "class") {
							reference {
								__typename
								... on Metaobject {
									...ClassScheduleAndBuilding
									# TODO Query the waitlist field once its shape is finalized.
								}
							}
						}
						product {
							courseOfferingMetafield: metafield(namespace: "custom", key: "course_offering") {
								reference {
									__typename
									... on Metaobject {
										modalityField: field(key: "modality") { value }
									}
								}
							}
						}
					}
				}
			}
			${CLASS_SCHEDULE_AND_BUILDING_FRAGMENT}
		`,
		{id: variantId}
	);

	if (!node) return null;
	if (node.__typename !== "ProductVariant") return null;
	const classInstance = castToMetaobject(node.classMetafield);
	const scheduleRaw = castToMetaobject(classInstance?.scheduleField);
	const meetingTimesRaw = castToMetaobjectArray(scheduleRaw?.meetingTimes);
	const building = castToMetaobject(classInstance?.campusBuildingField);
	const offering = castToMetaobject(node.product.courseOfferingMetafield);

	let schedule: VariantClassSchedule | null = null;
	if (scheduleRaw) {
		schedule = {
			classId: getValue(classInstance?.classIdField),
			dateStartAt: getValue(scheduleRaw.dateStartAt),
			dateEndAt: getValue(scheduleRaw.dateEndAt),
			timezone: getValue(scheduleRaw.timezoneField),
			meetingTimes: meetingTimesRaw.map((meeting) => ({
				day: getValue(meeting.day),
				timeStartAt: getValue(meeting.timeStartAt),
				timeEndAt: getValue(meeting.timeEndAt),
			})),
		};
	}

	return {
		id: node.id,
		availableForSale: node.availableForSale,
		schedule,
		campusBuildingId: getValue(building?.buildingIdField),
		modality: getValue(offering?.modalityField),
	};
}

/**
 * `cartLinesAdd` does not have an arg for cart attributes, so use this function
 * to set them.
 * @throws on Shopify `userErrors`
 */
async function setCartAttributes(
	cartId: string,
	attributes: CartAttribute[]
): Promise<{checkoutUrl: string}> {
	const data = await makeShopifyRequest<CartAttributesUpdateMutation>(
		"storefront",
		`#graphql
			mutation CartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
				cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
					cart {
						id
						checkoutUrl
					}
					userErrors {
						message
					}
				}
			}
		`,
		{
			cartId,
			attributes: attributes.map(({label, value}) => ({key: label, value})),
		}
	);

	const errors = data.cartAttributesUpdate?.userErrors ?? [];
	if (errors.length) {
		throw new Error(
			`Failed to update cart attributes: ${errors.map((e) => e.message).join("; ")}`
		);
	}

	const cart = data.cartAttributesUpdate?.cart;
	if (!cart) {
		throw new Error("Shopify returned no cart");
	}

	return {checkoutUrl: cart.checkoutUrl};
}

/**
 * Make sure to validate your input with `planAddToCart` prior to calling `addToShopifyCart`
 * because `addToShopifyCart` is only supposed to send requests to Shopify (imperative shell).
 */
export async function addToShopifyCart({
	cart: plannedCart,
	email,
}: {
	cart: CartCreationInput;
	email: string;
}): Promise<{checkoutUrl: string; itemCount: number}> {
	log.debug(
		{
			module: "packages/shopify/src/operations/add-to-cart.ts",
			fn: "addToShopifyCart",
			label: "D_SHOPIFY_CUSTOMER_LOOKUP",
			email,
			lineCount: plannedCart.items.length,
		},
		"Looking up Shopify Customer"
	);
	const customer = await getCustomerWithCartMetafield(email);
	if (!customer) {
		/* Do not initialize a new instance of a customer here because OIDC should
		have created one when it authenticates the customer to Shopify on login. */
		log.error(
			{
				module: "packages/shopify/src/operations/add-to-cart.ts",
				fn: "addToShopifyCart",
				label: "E_NO_CUSTOMER",
				email,
			},
			"No Shopify Customer found for shopper"
		);
		throw new NoShopifyCustomerError();
	}

	let cart;
	if (customer.cartId) {
		cart = await getCart(customer.cartId);
	}
	if (cart) {
		log.debug(
			{
				module: "packages/shopify/src/operations/add-to-cart.ts",
				fn: "addToShopifyCart",
				label: "D_SHOPIFY_ADD_LINES",
				cartId: cart.id,
			},
			"Adding lines to the existing cart"
		);
		const updatedCart = await addCartLines(cart.id, plannedCart.items);
		if (plannedCart.attributes?.length) {
			await setCartAttributes(cart.id, plannedCart.attributes);
		}
		const itemCount = updatedCart.lines.reduce(
			(total, line) => total + line.quantity,
			0
		);
		return {checkoutUrl: updatedCart.checkoutUrl, itemCount};
	}
	if (customer.cartId) {
		log.warn(
			{
				module: "packages/shopify/src/operations/add-to-cart.ts",
				fn: "addToShopifyCart",
				label: "W_INVALID_CART",
				customer,
			},
			"Customer cart expired; created new one"
		);
	}

	log.debug(
		{
			module: "packages/shopify/src/operations/add-to-cart.ts",
			fn: "addToShopifyCart",
			label: "D_SHOPIFY_CREATE_CART",
			customerShopifyId: customer.customerShopifyId,
		},
		"Creating a new Shopify cart"
	);
	const {id, checkoutUrl} = await createCart({...plannedCart, email});
	await setCustomerCartMetafield(customer.customerShopifyId, id);
	// createCart does not return lines; sum the planned quantities instead.
	const itemCount = plannedCart.items.reduce(
		(total, item) => total + item.quantity,
		0
	);
	return {checkoutUrl, itemCount};
}
